import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft } from "lucide-react";

export default function RabbitDetail() {
  const { id } = useParams();
  const [rabbit, setRabbit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/rabbits/${id}`).then((r) => setRabbit(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-16 text-center text-stone">Loading…</div>;
  if (!rabbit) return <div className="p-16 text-center text-stone">Rabbit not found.</div>;

  const available = rabbit.status === "available";

  return (
    <div data-testid={`page-rabbit-detail-${rabbit.id}`}>
      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
        <Link to="/rabbits" className="inline-flex items-center gap-2 text-sm text-stone link-underline" data-testid="back-to-rabbits">
          <ArrowLeft size={14} /> Back to all rabbits
        </Link>
      </div>
      <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-20 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] overflow-hidden border border-border bg-muted">
            {rabbit.image_url && (
              <img src={rabbit.image_url} alt={rabbit.name} className="h-full w-full object-cover" />
            )}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="section-label">{rabbit.sex} · {rabbit.color}</div>
          <h1 className="mt-3 font-serif text-5xl tracking-tight">{rabbit.name}</h1>
          <div className="mt-2 inline-block bg-bark px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-cream">
            {rabbit.status}
          </div>
          <div className="mt-8 font-serif text-4xl text-terracotta">${rabbit.price?.toFixed(0)}</div>

          <dl className="mt-10 grid grid-cols-2 gap-y-4 border-y border-border py-6 text-sm">
            <dt className="text-stone">Sex</dt><dd className="text-right">{rabbit.sex}</dd>
            <dt className="text-stone">Color</dt><dd className="text-right">{rabbit.color}</dd>
            {rabbit.birth_date && (<><dt className="text-stone">Born</dt><dd className="text-right">{rabbit.birth_date}</dd></>)}
            {rabbit.pedigree && (<><dt className="text-stone">Pedigree</dt><dd className="text-right">{rabbit.pedigree}</dd></>)}
          </dl>

          {rabbit.description && (
            <p className="mt-8 text-base leading-relaxed text-foreground/80">{rabbit.description}</p>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            {available ? (
              <Link
                to={`/reserve/${rabbit.id}`}
                data-testid={`reserve-cta-${rabbit.id}`}
                className="rounded-full bg-terracotta px-7 py-3.5 text-sm text-white transition-transform hover:-translate-y-0.5"
              >
                Reserve {rabbit.name}
              </Link>
            ) : (
              <button disabled className="cursor-not-allowed rounded-full bg-stone px-7 py-3.5 text-sm text-white opacity-60" data-testid="reserve-unavailable">
                Not currently available
              </button>
            )}
            <Link to="/contact" className="rounded-full border border-bark px-7 py-3.5 text-sm text-bark transition hover:bg-bark hover:text-cream" data-testid="ask-question-cta">
              Ask a question
            </Link>
          </div>

          <div className="mt-10 border border-border bg-muted p-6 text-sm text-foreground/80">
            <div className="section-label mb-2">Pickup only</div>
            We do not ship live rabbits. Local pickup at our Alabama rabbitry, by appointment only.
          </div>
        </div>
      </div>
    </div>
  );
}
